/**
 * One declared system in the sheet (YAG-1108): its YAML, saved whole, and
 * Delete in the More menu, asked once in the sheet. A new system takes a name
 * and starts from the template, which lists the workspace's repositories with
 * nothing picked.
 *
 * Validate now (YAG-1109) runs each member's environment once inside the
 * system, without repair, and the sheet lists each member's latest result,
 * reading the system again while any is still running.
 */
import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { Btn, MenuItem, RecordHead, SheetFoot, Stamp } from "../queue/index.js";
import { Button } from "../ui/index.js";
import { RUN_STATUS_WORDS, formatEnvironmentDate, systemAloneWords } from "./environmentPage.js";
import {
  SYSTEM_CHANGED,
  SYSTEM_CONFIG_UNREADABLE,
  SYSTEM_NAME_PATTERN,
  SystemsApiError,
  createSystem,
  deleteSystem,
  readSystem,
  readSystemTemplate,
  systemErrorOf,
  updateSystem,
  validateSkipWord,
  validateSystem,
  type DeclaredSystem,
  type DeclaredSystemSummary,
  type DeclaredSystemValidateResult,
  type DeclaredSystemValidation,
} from "./systemsApi.js";
import { YamlEditor, type YamlEditorError } from "./YamlEditor.js";
import "../workers/workers.css";
import "./repositoryEnvironment.css";

export const NEW_SYSTEM_ORIGIN = "Describe which repositories run together.";
export const SYSTEM_NAME_RULE =
  "Name the system with lowercase letters, digits and dashes, up to 40, starting with a letter or digit.";

export function systemOriginLine(system: Pick<DeclaredSystem, "declaredBy" | "revision">): string {
  return `Declared by ${system.declaredBy?.name ?? "a person"}, revision ${system.revision}.`;
}

export function teamsLine(teams: DeclaredSystemSummary["teams"]): string {
  return teams.length > 0 ? teams.map((team) => team.name).join(", ") : "No Team yet";
}

export function reposTag(repos: readonly string[]): string {
  return `${repos.length} ${repos.length === 1 ? "repository" : "repositories"}`;
}

/** What a system's Validate now does, beside its button. */
export const SYSTEM_VALIDATE_HELP =
  "Runs tools, setup, services and verify once for each member, inside this system. Nothing is repaired.";

/** How often the sheet reads the system again while a member's run is going. */
export const SYSTEM_VALIDATE_POLL_MS = 4000;

const VALIDATION_MARKS: Record<DeclaredSystemValidation["status"], string> = {
  ready: "✓",
  needs_attention: "!",
  validating: "·",
};

const VALIDATION_CLASSES: Record<DeclaredSystemValidation["status"], string> = {
  ready: "is-ok",
  needs_attention: "is-missing",
  validating: "is-running",
};

/**
 * A read, unless it is older than what the sheet holds: a poll sent before a
 * save can land after it, and must not put the old text back.
 */
export function newerOf(current: DeclaredSystem | null, read: DeclaredSystem): DeclaredSystem {
  return current && read.revision < current.revision ? current : read;
}

/**
 * A member's run that went ahead without the system (the system was deleted,
 * unreadable, or no longer lists it), with why; null when it ran inside it or
 * the server does not say.
 */
function ranAlone(entry: DeclaredSystemValidation): { reason?: string } | null {
  const facts = entry as DeclaredSystemValidation & { applied?: boolean; reason?: string };
  return facts.applied === false ? { reason: facts.reason } : null;
}

/** The repository sheet that holds a member's steps. */
function repositoryHref(fullName: string): string {
  return `/services/repositories?repository=${encodeURIComponent(fullName)}`;
}

/** Validate now and each member's latest result inside the system. */
function SystemValidate({
  system,
  disabled,
  onStarted,
}: {
  system: DeclaredSystem;
  disabled: boolean;
  /** Runs started: read the system again. A failed read is said here. */
  onStarted: () => Promise<void>;
}) {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState<DeclaredSystemValidateResult | null>(null);
  const validations = system.validations ?? [];
  const running = validations.some((entry) => entry.status === "validating");

  async function start() {
    setStarting(true);
    setError(null);
    try {
      setStarted(await validateSystem(system.id));
      await onStarted();
    } catch (err) {
      setError(systemErrorOf(err).message);
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="blk" data-testid="system-validate">
      <div className="bh">
        <span className="label">Validate</span>
        <span className="fact">each member, inside this system</span>
      </div>
      <span className="qa-hint">{SYSTEM_VALIDATE_HELP}</span>
      <span>
        <Button
          variant="secondary"
          disabled={disabled || starting || running}
          onClick={() => void start()}
        >
          Validate now
        </Button>
      </span>
      {error && (
        <p role="alert" className="err-line">
          {error}
        </p>
      )}
      {started && started.runs.length === 0 && (
        <p role="status" className="qa-hint">
          No member could be validated.
        </p>
      )}
      {validations.length > 0 && (
        <ol
          className="wrk-preflight-list qa-checklist"
          aria-label="Validation results"
          data-testid="system-validations"
        >
          {validations.map((entry) => (
            <li
              key={entry.repoId}
              data-state={entry.status}
              className={VALIDATION_CLASSES[entry.status]}
            >
              <span className="wrk-preflight-mark" aria-hidden="true">
                {VALIDATION_MARKS[entry.status]}
              </span>
              <span className="wrk-preflight-text">
                <Link to={repositoryHref(entry.fullName)}>{entry.fullName}</Link>
                <span className="wrk-preflight-fix">
                  {RUN_STATUS_WORDS[entry.status]}
                  {entry.finishedAt && entry.status !== "validating"
                    ? ` · ${formatEnvironmentDate(entry.finishedAt)}`
                    : ""}
                </span>
                {ranAlone(entry) && (
                  <span className="wrk-preflight-fix" data-testid="system-validation-alone">
                    ran alone: {systemAloneWords(ranAlone(entry)?.reason)}
                  </span>
                )}
                {!ranAlone(entry) && entry.composeSkipped === "target_holds_compose" && (
                  <span className="wrk-preflight-fix" data-testid="system-validation-compose">
                    compose services not started: this repository holds the system's compose file
                  </span>
                )}
                {entry.message && entry.status !== "ready" && (
                  <span className="wrk-preflight-fix">{entry.message}</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}
      {started && started.skipped.length > 0 && (
        <ul className="qa-run-notes" aria-label="Not validated" data-testid="system-skipped">
          {started.skipped.map((entry) => (
            <li key={entry.fullName}>
              {entry.fullName}: {validateSkipWord(entry.reason)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One saved system: read on open, saved in place by its revision. */
export function SystemSheet({
  summary,
  onChanged,
  onClose,
}: {
  summary: DeclaredSystemSummary;
  /** A write landed: re-read the list. */
  onChanged: () => void;
  onClose: () => void;
}) {
  const [system, setSystem] = useState<DeclaredSystem | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<YamlEditorError | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    readSystem(summary.id).then(
      (read) => {
        if (!cancelled) setSystem(read);
      },
      (err: unknown) => {
        if (!cancelled) setReadError(systemErrorOf(err).message);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [summary.id]);

  async function save(text: string): Promise<boolean> {
    if (!system || busy) return false;
    setBusy(true);
    setError(null);
    try {
      const saved = await updateSystem(system.id, { text, expectedRevision: system.revision });
      // The save's answer carries no validations (only a single read does):
      // keep the results already on the sheet.
      setSystem({ ...saved, validations: saved.validations ?? system.validations });
      onChanged();
      return true;
    } catch (err) {
      setError(systemErrorOf(err));
      // Someone saved first: take their revision so the next Save overwrites
      // it. The box keeps the person's text (it holds an edit over new server
      // text), and the error line says so.
      if (err instanceof SystemsApiError && err.code === SYSTEM_CHANGED) void reread();
      return false;
    } finally {
      setBusy(false);
    }
  }

  function take(read: DeclaredSystem) {
    setSystem((current) => newerOf(current, read));
  }

  async function reread() {
    try {
      take(await readSystem(summary.id));
    } catch (err) {
      setError(systemErrorOf(err));
    }
  }

  // A member's run is going: read the system again until every one settles.
  // A missed read is quiet; the next tick tries again.
  const validating = Boolean(system?.validations?.some((entry) => entry.status === "validating"));
  // Paused while a save or a delete is in flight.
  useEffect(() => {
    if (!validating || busy) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      readSystem(summary.id).then(
        (read) => {
          if (!cancelled) setSystem((current) => newerOf(current, read));
        },
        () => undefined,
      );
    }, SYSTEM_VALIDATE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [validating, busy, summary.id]);

  async function remove() {
    setBusy(true);
    setDeleteError(null);
    try {
      await deleteSystem(summary.id);
      onClose();
      onChanged();
    } catch (err) {
      setDeleteError(systemErrorOf(err).message);
    } finally {
      setBusy(false);
    }
  }

  const shown = system ?? summary;
  return (
    <>
      <RecordHead
        recordKey="SYSTEM"
        title={shown.name}
        onClose={onClose}
        more={<MenuItem onSelect={() => setConfirming(true)}>Delete this system</MenuItem>}
        meta={
          <span className="fact" style={{ fontSize: 11.5 }}>
            {reposTag(shown.repos)} · {teamsLine(shown.teams)}
          </span>
        }
      />
      <div className="sbody" data-testid={`system-sheet-${summary.id}`}>
        {confirming && (
          <div className="stopbox" data-testid="system-delete-confirm">
            <div className="h">
              <Stamp>delete</Stamp>
              Delete {shown.name}?
            </div>
            <p>
              {shown.teams.length > 0
                ? `${teamsLine(shown.teams)} ${shown.teams.length === 1 ? "picks" : "pick"} it and will build without a system.`
                : "No Team picks it."}
            </p>
            {deleteError && (
              <p role="alert" className="err-line">
                {deleteError}
              </p>
            )}
            <span style={{ display: "flex", gap: 8 }}>
              <Btn size="sm" variant="secondary" disabled={busy} onClick={() => void remove()}>
                Delete
              </Btn>
              <Btn size="sm" disabled={busy} onClick={() => setConfirming(false)}>
                Keep it
              </Btn>
            </span>
          </div>
        )}
        {shown.configUnreadable && (
          <p role="alert" className="qa-notice" data-testid="system-config-unreadable">
            {SYSTEM_CONFIG_UNREADABLE}
          </p>
        )}
        {readError && (
          <p role="alert" className="err-line">
            {readError}
          </p>
        )}
        {system ? (
          <YamlEditor
            title="System"
            fileName={`${system.name}.system.yml`}
            originLine={systemOriginLine(system)}
            text={system.yaml.text}
            revision={system.revision}
            busy={busy}
            error={error}
            saveLabel="Save"
            onSave={save}
            testIdPrefix="system-yaml"
          />
        ) : (
          !readError && <p className="fact">Reading the system.</p>
        )}
        {system && (
          <SystemValidate
            system={system}
            disabled={busy || Boolean(system.configUnreadable)}
            onStarted={async () => take(await readSystem(summary.id))}
          />
        )}
      </div>
      <SheetFoot
        why={
          <>
            A build on a Team that picks this system starts with <b>every repository it lists</b>.
          </>
        }
      />
    </>
  );
}

/** A new system: a name, and the template to start from. */
export function NewSystemSheet({
  onCreated,
  onCancel,
}: {
  onCreated: (system: DeclaredSystem) => void;
  onCancel: () => void;
}) {
  const nameId = useId();
  const [name, setName] = useState("");
  const [template, setTemplate] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<YamlEditorError | null>(null);

  useEffect(() => {
    let cancelled = false;
    readSystemTemplate().then(
      (text) => {
        if (!cancelled) setTemplate(text);
      },
      (err: unknown) => {
        if (cancelled) return;
        setTemplate("repos:\n");
        setError({ message: `The template could not be read: ${systemErrorOf(err).message}` });
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  async function save(text: string): Promise<boolean> {
    if (busy) return false;
    const trimmed = name.trim();
    if (!SYSTEM_NAME_PATTERN.test(trimmed)) {
      setError({ message: SYSTEM_NAME_RULE });
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      onCreated(await createSystem({ name: trimmed, text }));
      return true;
    } catch (err) {
      setError(systemErrorOf(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <RecordHead
        recordKey="NEW SYSTEM"
        title="New system"
        onClose={onCancel}
        noExpand
        meta={
          <span className="fact" style={{ fontSize: 11.5 }}>
            A name and the repositories that run together. A Team picks it in its settings.
          </span>
        }
      />
      <div className="sbody" data-testid="system-new-sheet">
        <div className="field">
          <label htmlFor={nameId}>Name</label>
          <div className="in mono">
            <input
              id={nameId}
              value={name}
              placeholder="storefront"
              maxLength={40}
              autoComplete="off"
              spellCheck={false}
              data-testid="system-name"
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        </div>
        {template === null ? (
          <p className="fact">Reading your repositories.</p>
        ) : (
          <YamlEditor
            title="System"
            fileName={`${name.trim() || "name"}.system.yml`}
            originLine={NEW_SYSTEM_ORIGIN}
            text={template}
            busy={busy}
            error={error}
            saveLabel="Save"
            saveUnchanged
            onSave={save}
            testIdPrefix="system-yaml"
          />
        )}
      </div>
      <SheetFoot
        why={
          <>
            Saving declares the system. <b>No Team uses it</b> until one picks it.
          </>
        }
      />
    </>
  );
}
